import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Hardcoded data
const userProgress = {
  completedCourses: 2,
  totalCourses: 5,
  completedModules: 8,
  totalModules: 15,
  phishingScore: 85,
};

const recentActivities = [
  { id: 1, type: 'course', name: 'Introduction to Phishing', date: '2023-10-15', status: 'completed' },
  { id: 2, type: 'module', name: 'Recognizing Phishing Emails', date: '2023-10-14', status: 'completed' },
  { id: 3, type: 'quiz', name: 'Email Security Quiz', date: '2023-10-12', status: 'completed' },
  { id: 4, type: 'simulation', name: 'Phishing Simulation #1', date: '2023-10-10', status: 'failed' },
];

const upcomingTrainings = [
  { id: 1, name: 'Advanced Phishing Techniques', dueDate: '2023-10-25' },
  { id: 2, name: 'Social Engineering Defense', dueDate: '2023-11-05' },
];

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        Welcome back, {currentUser.name}!
      </h1>
      
      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card bg-blue-50 border-l-4 border-blue-500">
          <h3 className="font-semibold text-lg text-gray-800">Courses</h3>
          <p className="text-2xl font-bold mt-2">
            {userProgress.completedCourses}/{userProgress.totalCourses}
          </p>
          <p className="text-sm text-gray-500 mt-1">Completed</p>
        </div>
        
        <div className="card bg-green-50 border-l-4 border-green-500">
          <h3 className="font-semibold text-lg text-gray-800">Modules</h3>
          <p className="text-2xl font-bold mt-2">
            {userProgress.completedModules}/{userProgress.totalModules}
          </p>
          <p className="text-sm text-gray-500 mt-1">Completed</p>
        </div>
        
        <div className="card bg-purple-50 border-l-4 border-purple-500">
          <h3 className="font-semibold text-lg text-gray-800">Phishing Score</h3>
          <p className="text-2xl font-bold mt-2">{userProgress.phishingScore}%</p>
          <p className="text-sm text-gray-500 mt-1">Awareness Level</p>
        </div>
        
        <div className="card bg-yellow-50 border-l-4 border-yellow-500">
          <h3 className="font-semibold text-lg text-gray-800">Next Due</h3>
          <p className="text-lg font-bold mt-2">
            {upcomingTrainings.length > 0 ? upcomingTrainings[0].name : 'None'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {upcomingTrainings.length > 0 ? `Due: ${upcomingTrainings[0].dueDate}` : 'No upcoming trainings'}
          </p>
        </div>
      </div>
      
      {/* Recent Activity */}
      <div className="card mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentActivities.map((activity) => (
                <tr key={activity.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{activity.name}</div>
                    <div className="text-xs text-gray-500">{activity.type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {activity.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      activity.status === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {activity.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Upcoming Trainings */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Trainings</h2>
          <Link to="/courses" className="text-sm text-primary-600 hover:text-primary-700">
            View all courses
          </Link>
        </div>
        
        {upcomingTrainings.length > 0 ? (
          <div className="space-y-4">
            {upcomingTrainings.map((training) => (
              <div key={training.id} className="border border-gray-200 rounded-md p-4 hover:bg-gray-50">
                <h3 className="font-medium text-gray-900">{training.name}</h3>
                <p className="text-sm text-gray-500 mt-1">Due by: {training.dueDate}</p>
                <div className="mt-3">
                  <Link 
                    to={`/courses/${training.id}`} 
                    className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Start Training →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No upcoming trainings scheduled.</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 